import React from 'react';
import {
  getActivityChangedFields,
  getActivityDetailTitle,
  getActivitySnapshotEntries,
} from '../../utils/activityLogFormatting';

const ObjectDiff = ({ before, after }) => {
  const changes = getActivityChangedFields(before, after);

  if (changes.length === 0) return <p className="text-sm text-gray-500 mt-2">No significant changes detected.</p>;

  return (
    <div className="text-xs space-y-2 mt-2">
      {changes.map((change) => (
        <div key={change.key} className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <strong className="font-semibold text-gray-800">{change.label}:</strong>
          <span className="text-red-600 ml-2 bg-red-50 px-1.5 py-0.5 rounded break-words">{change.before}</span>
          <span className="mx-2 font-bold">→</span>
          <span className="text-green-600 bg-green-50 px-1.5 py-0.5 rounded break-words">{change.after}</span>
        </div>
      ))}
    </div>
  );
};

const SnapshotViewer = ({ snapshot, tone = 'neutral' }) => {
  const entries = getActivitySnapshotEntries(snapshot);
  const toneClasses = tone === 'danger' ? 'bg-red-50 border-red-100' : 'bg-white border-gray-200';

  if (entries.length === 0) {
    return <p className="text-sm text-gray-500 mt-2">No additional details available.</p>;
  }

  return (
    <div className={`mt-2 rounded-md border ${toneClasses} overflow-hidden`}>
      <dl className="divide-y divide-gray-200">
        {entries.map((entry) => (
          <div key={entry.key} className="grid gap-2 px-3 py-2 sm:grid-cols-[180px_minmax(0,1fr)]">
            <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{entry.label}</dt>
            <dd className="text-sm text-gray-800 break-words">{entry.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const LogDetailViewer = ({ log }) => {
  const renderContent = () => {
    switch (log.action) {
      case 'PRODUCT_CREATE':
      case 'SALE_PROCESS':
      case 'USER_REGISTER':
      case 'CUSTOMER_CREATE':
      case 'PROMOTION_CREATE':
      case 'REFUND_PROCESS':
      case 'STORE_CREATE':
      case 'STORE_SETTINGS_CREATE':
      case 'TRANSFER_CREATE':
      case 'PRODUCT_BULK_UPDATE_UPLOAD':
      case 'PRODUCT_BULK_UPDATE_VALIDATE':
      case 'PRODUCT_BULK_UPDATE_APPLY':
      case 'PRODUCT_BULK_UPDATE_ROLLBACK':
        return <SnapshotViewer snapshot={log.after} />;

      case 'PRODUCT_UPDATE':
      case 'CUSTOMER_UPDATE':
      case 'USER_UPDATE':
      case 'USER_DEACTIVATE':
      case 'USER_REACTIVATE':
      case 'PROMOTION_UPDATE':
      case 'STORE_UPDATE':
      case 'STORE_SETTINGS_UPDATE':
      case 'INVOICE_UPDATE':
      case 'INVOICE_STATUS_UPDATE':
      case 'PURCHASE_ORDER_STATUS_UPDATE':
      case 'TRANSFER_UPDATE':
        return <ObjectDiff before={log.before} after={log.after} />;

      case 'PRODUCT_DELETE':
      case 'CUSTOMER_DELETE':
      case 'USER_DELETE':
      case 'PROMOTION_DELETE':
      case 'STORE_DELETE':
      case 'STORE_SETTINGS_DELETE':
        return <SnapshotViewer snapshot={log.before} tone="danger" />;

      default:
        if (log.after) return <SnapshotViewer snapshot={log.after} />;
        if (log.before) return <SnapshotViewer snapshot={log.before} />;
        return <p className="text-sm text-gray-500 mt-2">No detail view available for this action type.</p>;
    }
  };

  return (
    <div className="p-4 bg-gray-50">
      <h4 className="font-bold text-gray-700">{getActivityDetailTitle(log)}</h4>
      {renderContent()}
    </div>
  );
};

export default LogDetailViewer;
